import { useState, useCallback } from "react"
import { Box, Typography } from "@mui/material"
import { OptimizeFormApi, useFormContext } from "connected-components/optimize-experiments/form-context"
import type { FactorRule } from "components/search-factor/types"
import type { MarketValues } from "../helpers/configuration"
import type { PositionSetInput, FactorRuleDisplayType, FactorRuleDisplay, FactorRuleField } from "./types"
import FactorRules from "./display"
import FactorRulesMenu from "./menu"
import FactorRulesSearchModal from "./search-modal"
import { FACTOR_RULE_DISPLAY_TYPES } from "./constants"

interface FactorRulesWrapperProps {
  positionSet: PositionSetInput
  portfolioMarketValues: MarketValues
  container?: HTMLDivElement | null
}

export default function FactorRulesWrapper({ positionSet, portfolioMarketValues, container }: FactorRulesWrapperProps) {
  const form: OptimizeFormApi = useFormContext()
  const [ruleDisplayType, setRuleDisplayType] = useState<FactorRuleDisplayType | null>(null)
  const [ruleMenuAnchor, setRuleMenuAnchor] = useState<null | HTMLElement>(null)

  const netMarketValue = portfolioMarketValues.longMarketValue - portfolioMarketValues.shortMarketValue

  const getRules = useCallback((ruleDisplayType: FactorRuleDisplayType): FactorRuleDisplay[] => {
    return form.state.values.rules[ruleDisplayType] || []
  }, [form])

  function addRule(rule: FactorRule, ruleDisplayType: FactorRuleDisplayType) {
    const updatedRules = [...getRules(ruleDisplayType), rule]
    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function updateRule(rule: FactorRuleDisplay, field: FactorRuleField, value: string, ruleDisplayType: FactorRuleDisplayType) {
    const numericValue = parseFloat(value)
    const isComposition = ruleDisplayType === FACTOR_RULE_DISPLAY_TYPES.COMPOSITION
    const ruleIndex = getRules(ruleDisplayType).findIndex(({ id, contentSetId }) => {
      if (rule.contentSetId) {
        return id === rule.id && contentSetId === rule.contentSetId
      }
      return id === rule.id
    })

    if (ruleIndex === -1) return

    const finalValue = !isNaN(numericValue) ? numericValue : undefined
    form.setFieldValue(`rules.${ruleDisplayType}[${ruleIndex}].${field}`, finalValue)

    // For composition rules, also update the economic exposure
    if (isComposition && !isNaN(numericValue) && netMarketValue !== null) {
      const economicExposure = parseFloat(((numericValue / 100) * netMarketValue).toFixed(2))
      form.setFieldValue(`rules.${ruleDisplayType}[${ruleIndex}].${field}EconomicExposure`, economicExposure)
    } else if (isComposition) {
      form.setFieldValue(`rules.${ruleDisplayType}[${ruleIndex}].${field}EconomicExposure`, undefined)
    }
  }

  function removeRule(rule: FactorRuleDisplay, ruleDisplayType: FactorRuleDisplayType) {
    const updatedRules = getRules(ruleDisplayType).filter(({ id, contentSetId }) => {
      if (rule.contentSetId) {
        return !(id === rule.id && contentSetId === rule.contentSetId)
      }
      return id !== rule.id
    })
    form.setFieldValue(`rules.${ruleDisplayType}`, updatedRules)
  }

  function handleOpenRuleSearch(ruleType: FactorRuleDisplayType) {
    setRuleDisplayType(ruleType)
    setRuleMenuAnchor(null)
  }

  return (
    <Box className="components_factor-rules">
      <Typography className="factor-rules-label">
        Exposure and Composition Targets
      </Typography>
      <Typography variant="caption" className="factor-rules-description">
        Specify desired exposures to key risk or alpha factors to guide portfolio tilts.
      </Typography>
      <FactorRulesMenu
        anchorEl={ruleMenuAnchor}
        onOpen={(event) => setRuleMenuAnchor(event.currentTarget)}
        onClose={() => setRuleMenuAnchor(null)}
        onSelectType={handleOpenRuleSearch}
      />
      <FactorRules
        exposure={form.state.values.rules.exposure || []}
        composition={form.state.values.rules.composition || []}
        updateFactorRule={updateRule}
        removeFactorRule={removeRule}
      />
      <FactorRulesSearchModal
        positionSet={positionSet}
        onRuleSelected={addRule}
        ruleDisplayType={ruleDisplayType || FACTOR_RULE_DISPLAY_TYPES.EXPOSURE}
        isOpen={ruleDisplayType !== null}
        onClose={() => setRuleDisplayType(null)}
        existingRules={ruleDisplayType ? form.state.values.rules[ruleDisplayType] || [] : []}
        container={container}
      />
    </Box>
  )
}
